import type { ActionFunction } from '~/types/route-args';

import type {
    CreateOrUpdateBuildHubMutation,
    CreateOrUpdateBuildHubMutationVariables,
    PlaceDetailTypeInput,
} from 'generated/graphql';
import { CreateOrUpdateBuildHubDocument } from 'generated/documents/CreateOrUpdateBuildHubDocument';
import invariant from 'tiny-invariant';
import type { BuildHubTypeEnum } from 'generated/graphql';

export const BUILD_HUB_UPSERT_API_ROUTE = '/remix-api/app/build-hub-create';

const DEFAULT_FAILED_MESSAGE = 'Failed to create BuildHub.';

/**
 * Helper to get FormData value if it exists and is not empty
 */
function getOptionalField(
    data: FormData,
    key: string,
): FormDataEntryValue | null {
    const value = data.get(key);
    return value && value !== '' ? value : null;
}

/**
 * Helper to parse JSON field from FormData
 */
function parseJsonField<T>(data: FormData, key: string): T | null {
    const jsonString = getOptionalField(data, key);
    if (!jsonString || typeof jsonString !== 'string') {
        return null;
    }

    try {
        return JSON.parse(jsonString) as T;
    } catch (error) {
        console.error(`Failed to parse ${key} JSON:`, error);
        return null;
    }
}

/**
 * Maps FormData fields to GraphQL variables for update operations
 */
function mapUpdateFields(
    data: FormData,
    variables: CreateOrUpdateBuildHubMutationVariables,
): void {
    // Simple string fields
    const stringFields: Array<
        [string, keyof CreateOrUpdateBuildHubMutationVariables]
    > = [
        ['title', 'Title'],
        ['description', 'MainDescription'],
        ['startDate', 'StartDate'],
        ['endDate', 'EndDate'],
        ['activeStageURLSegment', 'ActiveStageURLSegment'],
    ];

    stringFields.forEach(([formKey, variableKey]) => {
        const value = getOptionalField(data, formKey);
        if (value && typeof value === 'string') {
            variables[variableKey] = value as never;
        }
    });

    // Location data (PlaceDetailTypeInput) - preferred method
    const locationData = parseJsonField<PlaceDetailTypeInput>(
        data,
        'locationData',
    );
    if (locationData) {
        variables.LocationData = locationData;
    }

    // Enum fields
    const projectType = getOptionalField(data, 'projectType');
    if (projectType) {
        variables.ProjectType = projectType as BuildHubTypeEnum;
    }

    const projectScope = getOptionalField(data, 'projectScope');
    if (projectScope && typeof projectScope === 'string') {
        variables.ProjectScope = projectScope;
    }

    // Number fields
    const budgetMin = getOptionalField(data, 'budget_min');
    if (budgetMin) {
        variables.BudgetMin = Number(budgetMin);
    }

    const budgetMax = getOptionalField(data, 'budget_max');
    if (budgetMax) {
        variables.BudgetMax = Number(budgetMax);
    }

    // Owner professional/company fields
    const ownerTeamMemberID = getOptionalField(data, 'ownerTeamMemberID');
    if (ownerTeamMemberID && typeof ownerTeamMemberID === 'string') {
        variables.OwnerTeamMemberID = parseInt(ownerTeamMemberID, 10);
    }

    const ownerCustomCompanyName = getOptionalField(
        data,
        'ownerCustomCompanyName',
    );
    if (ownerCustomCompanyName && typeof ownerCustomCompanyName === 'string') {
        variables.OwnerCustomCompanyName = ownerCustomCompanyName;
    }

    const creationSource = getOptionalField(data, 'creationSource');
    if (creationSource && typeof creationSource === 'string') {
        variables.CreationSource =
            creationSource as CreateOrUpdateBuildHubMutationVariables['CreationSource'];
    }

    // File upload
    const thumbnailFile = data.get('thumbnailFile');
    if (thumbnailFile && thumbnailFile instanceof File) {
        variables.ThumbnailFile = thumbnailFile;
    }

    // Stages array (JSON)
    const stages = parseJsonField<Array<{ urlSegment: string; title: string }>>(
        data,
        'stages',
    );
    if (stages && Array.isArray(stages)) {
        variables.Stages = stages;
    }
}

export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();
    const intent = data.get('intent'); // 'create', 'update', or 'upload-image'

    let variables: CreateOrUpdateBuildHubMutationVariables;

    if (intent === 'create') {
        // For creation, title is required
        invariant(data.get('title'), 'No title submitted.');

        variables = {
            Title: data.get('title'),
        };

        // Map all additional fields sent during creation
        mapUpdateFields(data, variables);
    } else if (intent === 'update') {
        // For updates, only ID is required
        invariant(data.get('id'), 'No ID submitted for update.');

        variables = {
            BuildHubID: data.get('id') as string,
        };

        // Map all update fields
        mapUpdateFields(data, variables);
    } else if (intent === 'upload-image') {
        // For image uploads, only ID and image file are required
        invariant(data.get('id'), 'No ID submitted for image upload.');
        invariant(data.get('thumbnailFile'), 'No image file submitted.');

        const thumbnailFile = data.get('thumbnailFile');
        invariant(
            thumbnailFile instanceof File,
            'thumbnailFile must be a File.',
        );

        variables = {
            BuildHubID: data.get('id') as string,
            ThumbnailFile: thumbnailFile,
        };
    } else if (intent === 'remove-image') {
        // For image removal, only ID is required
        invariant(data.get('id'), 'No ID submitted for image removal.');

        variables = {
            BuildHubID: data.get('id') as string,
            RemoveThumbnail: true,
        };
    } else {
        throw new Error(
            'Invalid intent. Must be "create", "update", "upload-image", or "remove-image".',
        );
    }

    try {
        const { data: result } = await context.client.mutate<
            CreateOrUpdateBuildHubMutation,
            CreateOrUpdateBuildHubMutationVariables
        >({
            mutation: CreateOrUpdateBuildHubDocument,
            variables,
        });

        const validResult = result?.buildHubUpsert;
        if (!validResult) {
            return {
                buildHubUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            };
        }

        return result;
    } catch (error) {
        return {
            buildHubUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        };
    }
};
